import React, { useMemo } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import { Ticket, Users, CalendarCheck, Boxes, Lock, UserCheck } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { TICKET_SALES_TREND } from "../../data/mockData";

const BRAND = ["#F2765A", "#4C8FD6", "#B4E04A", "#FBB03B", "#6FBE4A", "#8FD9F2"];

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="mt-3 text-3xl font-display text-slate-800">{value}</p>
      <p className="text-sm font-semibold text-slate-500">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { tickets, activities, slotBooked, slotRemaining, activityStatus } = useApp();

  const stats = useMemo(() => {
    const paid = tickets.filter((t) => t.paymentStatus === "Paid");
    const allBookings = tickets.flatMap((t) => t.bookings.filter((b) => b.status === "Confirmed"));
    const availableSlots = activities.filter((a) => a.bookingRequired)
      .reduce((s, a) => s + a.slots.reduce((ss, sl) => ss + slotRemaining(a.id, sl.id), 0), 0);
    const fullyBooked = activities.filter((a) => activityStatus(a) === "fully-booked").length;
    const checkedIn = tickets.flatMap((t) => t.bookings).filter((b) => b.checkIn === "Checked In").length;
    return {
      sold: paid.length,
      participants: paid.length,
      bookings: allBookings.length,
      availableSlots,
      fullyBooked,
      checkedIn,
    };
  }, [tickets, activities, slotRemaining, activityStatus]);

  const byActivity = useMemo(() =>
    activities.filter((a) => a.bookingRequired).map((a) => ({
      name: a.name.split(" ")[0],
      bookings: a.slots.reduce((s, sl) => s + slotBooked(a.id, sl.id), 0),
    })), [activities, slotBooked]);

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl text-slate-800">Dashboard</h1>
      <p className="text-slate-500 font-semibold">Ringkasan operasional BLTR 2026.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Ticket} label="Tickets Sold" value={stats.sold} tint="bg-coral/15 text-coral" />
        <StatCard icon={Users} label="Total Participants" value={stats.participants} tint="bg-sea/15 text-sea" />
        <StatCard icon={CalendarCheck} label="Activity Bookings" value={stats.bookings} tint="bg-grass/15 text-grass" />
        <StatCard icon={Boxes} label="Available Slots" value={stats.availableSlots} tint="bg-sunny/20 text-sunny-dark" />
        <StatCard icon={Lock} label="Fully Booked" value={stats.fullyBooked} tint="bg-destructive/10 text-destructive" />
        <StatCard icon={UserCheck} label="Checked-In" value={stats.checkedIn} tint="bg-sky/25 text-sky-dark" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-display text-lg text-slate-800">Ticket Purchases Over Time</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TICKET_SALES_TREND} margin={{ left: -20, right: 8 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F2765A" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#F2765A" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip />
                <Area type="monotone" dataKey="tickets" stroke="#F2765A" strokeWidth={3} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-display text-lg text-slate-800">Bookings by Activity</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byActivity} margin={{ left: -20, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip />
                <Bar dataKey="bookings" radius={[8, 8, 0, 0]}>
                  {byActivity.map((_, i) => <Cell key={i} fill={BRAND[i % BRAND.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
